import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageTypes, sendToNative, subscribeToNative, supportsIndoorPositioning } from '../nativeBridge';
import GeneralHelper from '../general';

/**
 * Indoor positioning orchestration.
 *
 * Positioning is computed natively by the CloudEngine SDK in the mobile shell
 * (hkust-path-mobile). With `autoStart` it runs whenever the app is on screen, the way a maps
 * app shows where you are without being asked; without it, it waits for `start()`. A session
 * is one of two kinds:
 *
 *   - Silent: nobody asked for it - `autoStart`, or a session resumed when the app comes back to
 *     the foreground. No spinner, no timeout and no error. If it fails the user simply sees no
 *     dot, and it is tried again the next time the app returns to the foreground.
 *   - Requested: `start()`, i.e. the user tapped "locate me" or picked "Current Location".
 *       1. `start()` raises `loading`. If a silent session is already under way it is joined
 *          rather than restarted - the user is already partway to a fix.
 *       2. Otherwise the shell runs a health check, registers, then scans and uploads until the
 *          server resolves a position. `loading` stays true for that whole stretch — the user is
 *          waiting to be located, and "registered" is not a milestone that means anything to them.
 *       3. `loading` clears on the first fix, or on failure, or after DETECT_TIMEOUT_MS,
 *          whichever comes first. A failure also surfaces `error` for the caller to show.
 *
 * Fixes outside the campus map area (MapDefaults.extent) are refused. A requested session ends and
 * `error` says the user is off campus; a silent one hides the dot and keeps listening, so walking
 * back onto campus brings it back. That can happen on the first fix or on a later one, so the
 * check runs on every position, not only while detecting.
 *
 * Positioning pauses while the page is hidden - the app in the background or the phone locked - so
 * the native scanner and its 1 Hz upload loop don't run for a map nobody is looking at. It picks up
 * again, silently, when the page is shown.
 *
 * No beacon scanning or position math happens in the web app.
 *
 * Options:
 *   autoStart    - keep positioning running whenever the page is visible
 *
 * Returns:
 *   available    - whether positioning can run (i.e. the shell hosting us ships the SDK;
 *                  being inside *a* native shell is not enough - see nativeBridge)
 *   position     - { longitude, latitude, accuracy, building_floor_id, building_id } | null
 *   heading      - { degrees, accuracy } the phone is pointing, or null when unknown/untrusted
 *   status       - last raw status string reported by native
 *   loading      - true while a requested detection is waiting for the user's position
 *   error        - human-readable error message to show, or null
 *   active       - true while a positioning session is running natively
 *   start()      - the user wants their position: joins the session under way, or starts one
 *   stop()       - stop positioning, and keep it stopped until the next `start()`
 *   clearError() - dismiss the current error
 */

const UNAVAILABLE_MESSAGE =
	'Positioning service is temporarily unavailable. Please try again later.';
const PERMISSION_DENIED_MESSAGE =
	'Location permission is needed to show where you are. Turn it on for PathAdvisor in Settings, then try again.';
// A fix can land outside the campus map — a GPS-only fix taken off campus, or one the server
// resolved from beacons that are no longer in range. There is no floor plan out there and the map
// cannot even pan to it, so a requested session ends rather than leaving a dot the user can never see.
const OUTSIDE_CAMPUS_MESSAGE =
	'You appear to be outside HKUST. PathAdvisor can only show your location while you are on campus.';

// How long the user is asked to wait before we call the attempt off. Covers the whole
// detection — health check, register, and then scanning until the server resolves a
// position. Native fails the health check and register after 5s each, so in practice
// only a server that accepts uploads but never answers with a fix runs this clock out.
const DETECT_TIMEOUT_MS = 20000;

// Worst heading error we are still willing to draw a direction cone for. Indoors a magnetometer
// sits inside a steel building next to lift motors and risers, and a cone that points confidently
// down the wrong corridor is worse than no cone at all - past this we show the plain dot instead.
const MAX_HEADING_ACCURACY_DEGREES = 35;

// How long the app can sit in the background before the last fix stops being a fair answer. Back
// from a quick look at another app, the dot stays put until the fresh fix lands a moment later;
// after longer the user may have walked anywhere, and "Current Location" must not route from there.
const STALE_FIX_AFTER_HIDDEN_MS = 30000;

export default function useIndoorPositioning({ autoStart = false } = {}) {
	const [available] = useState(supportsIndoorPositioning());
	const [position, setPosition] = useState(null);
	const [heading, setHeading] = useState(null);
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [active, setActive] = useState(false);

	const timeoutRef = useRef(null);
	// True from the start of a session until it has a usable fix (or ends). Keeps a
	// failure reported as two statuses — native follows 'denied' with 'health_failed' —
	// from replacing the first, more specific message with the generic one.
	const detectingRef = useRef(false);
	// True from the start of a session until it ends or pauses, however that happens. Fixes keep
	// streaming at 1 Hz for the moment it takes native to wind down, and each one would otherwise
	// re-raise the error the session just ended with, or put back a dot that was just taken away.
	const sessionRef = useRef(false);
	// Whether the session under way is one nobody asked for. `loading` is exactly "detecting and not
	// silent", which is what lets the visibility handler below work from refs alone.
	const silentRef = useRef(false);
	// Set when the page is hidden with a session under way, so it can be picked up again when the
	// page is shown - as requested as it was, if the user was still waiting for their fix.
	const pausedRef = useRef(null);
	const hiddenAtRef = useRef(null);
	// The user refused location. Starting again on our own would put the permission dialog back in
	// front of them every time they return to the app (Android asks again until told not to), so
	// only their own `start()` tries again.
	const deniedRef = useRef(false);
	// `stop()` was called, so nothing restarts positioning behind the caller's back.
	const stoppedRef = useRef(false);

	const clearDetectTimer = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	// End the session, with a message for the user if they asked for it.
	const endSession = useCallback((message) => {
		if (!sessionRef.current) return;
		sessionRef.current = false;
		detectingRef.current = false;
		clearDetectTimer();
		setLoading(false);
		setActive(false);
		setPosition(null);
		setHeading(null);
		// A silent session fails silently: nobody is waiting on it, and the user just sees no dot.
		if (!silentRef.current) setError(message);
		// Don't leave the native scanner and its 1 Hz upload loop running behind a
		// dead UI — the next tap starts a fresh session anyway.
		sendToNative({ type: MessageTypes.STOP_POSITIONING });
	}, [clearDetectTimer]);

	// End the current attempt with a message for the user. Ignored once the attempt has
	// already finished, so a late status can't resurrect an error the user dismissed.
	const failDetection = useCallback((message) => {
		if (!detectingRef.current) return;
		endSession(message);
	}, [endSession]);

	// The user is now waiting: show the spinner and start the clock on their wait.
	const awaitRequestedFix = useCallback(() => {
		setError(null);
		setLoading(true);
		clearDetectTimer();
		timeoutRef.current = setTimeout(() => {
			timeoutRef.current = null;
			failDetection(UNAVAILABLE_MESSAGE);
		}, DETECT_TIMEOUT_MS);
	}, [clearDetectTimer, failDetection]);

	// Ask the shell for a fresh session. Native restarts one that is already running cleanly.
	const beginSession = useCallback((requested) => {
		sessionRef.current = true;
		detectingRef.current = true;
		silentRef.current = !requested;
		if (requested) {
			setPosition(null);
			awaitRequestedFix();
		}
		sendToNative({ type: MessageTypes.START_POSITIONING });
	}, [awaitRequestedFix]);

	const clearError = useCallback(() => setError(null), []);

	// Subscribe to native messages for the whole lifetime of the hook.
	useEffect(() => {
		if (!available) return undefined;

		const unsubscribe = subscribeToNative((msg) => {
			switch (msg.type) {
				case MessageTypes.POSITION: {
					const pos = msg.position;
					if (!sessionRef.current) break;
					if (pos && typeof pos.longitude === 'number' && typeof pos.latitude === 'number') {
						// Off the campus map: nothing downstream can do anything with this fix.
						if (!GeneralHelper.isWithinMapExtent(pos.longitude, pos.latitude)) {
							// Nobody asked, so there is nobody to tell - just stop showing a dot and
							// keep listening. A user who asks from here gets the explanation.
							if (silentRef.current) {
								detectingRef.current = true;
								setPosition(null);
								break;
							}
							endSession(OUTSIDE_CAMPUS_MESSAGE);
							break;
						}

						// The fix the user was waiting for: stop the spinner and the clock.
						if (detectingRef.current) {
							detectingRef.current = false;
							clearDetectTimer();
							setLoading(false);
						}
						setActive(true);
						setPosition({
							longitude: pos.longitude,
							latitude: pos.latitude,
							accuracy: typeof pos.accuracy === 'number' ? pos.accuracy : undefined,
							// PathAdvisor ids, already resolved by the native shell. Both are null
							// when the fix could not be placed on a floor (e.g. GPS-only outdoors).
							building_floor_id: pos.building_floor_id ? String(pos.building_floor_id) : null,
							building_id: pos.building_id ? String(pos.building_id) : null
						});
					}
					break;
				}
				case MessageTypes.HEADING: {
					const degrees = msg.heading;
					if (typeof degrees !== 'number' || Number.isNaN(degrees)) break;

					// Native reports a negative accuracy when the compass has no usable estimate.
					const accuracy = typeof msg.accuracy === 'number' ? msg.accuracy : -1;
					if (accuracy < 0 || accuracy > MAX_HEADING_ACCURACY_DEGREES) {
						setHeading(null);
						break;
					}

					setHeading({ degrees: ((degrees % 360) + 360) % 360, accuracy });
					break;
				}
				case MessageTypes.POSITION_STATUS: {
					const next = msg.status || null;
					setStatus(next);
					switch (next) {
						case 'ready':
							// Registered and scanning. Keep `loading` up: from here we are
							// waiting on the first fix, which is what the user asked for.
							setActive(true);
							break;
						case 'health_failed':
						case 'register_failed':
							failDetection(UNAVAILABLE_MESSAGE);
							break;
						case 'denied':
							// Without location there is neither beacon ranging nor GPS, so no
							// fix will ever arrive; say what to do about it rather than blaming
							// the server. Native follows this with 'health_failed', which
							// `failDetection` then ignores.
							deniedRef.current = true;
							failDetection(PERMISSION_DENIED_MESSAGE);
							break;
						default:
							// 'searching' and the remaining sensor statuses are informational:
							// Bluetooth being off still leaves a coarse GPS fix possible.
							break;
					}
					break;
				}
				default:
					break;
			}
		});

		return () => {
			clearDetectTimer();
			detectingRef.current = false;
			sessionRef.current = false;
			pausedRef.current = null;
			sendToNative({ type: MessageTypes.STOP_POSITIONING });
			unsubscribe();
		};
	}, [available, clearDetectTimer, failDetection, endSession]);

	// Pause while the page is hidden, and pick up again when it is shown.
	useEffect(() => {
		if (!available) return undefined;

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				hiddenAtRef.current = Date.now();
				if (!sessionRef.current) return;

				pausedRef.current = { requested: !silentRef.current && detectingRef.current };
				// Stood down rather than ended: no error, and the dot, `loading` and `active` stay
				// as they were, for the moment the page is shown again.
				sessionRef.current = false;
				detectingRef.current = false;
				clearDetectTimer();
				sendToNative({ type: MessageTypes.STOP_POSITIONING });
				return;
			}

			const paused = pausedRef.current;
			pausedRef.current = null;

			if (hiddenAtRef.current && Date.now() - hiddenAtRef.current > STALE_FIX_AFTER_HIDDEN_MS) {
				setPosition(null);
				setHeading(null);
			}
			hiddenAtRef.current = null;

			if (paused) {
				beginSession(paused.requested);
			} else if (autoStart && !sessionRef.current && !deniedRef.current && !stoppedRef.current) {
				// Nothing was running: the last attempt failed, or the page loaded while hidden.
				// Coming back is a good moment to try again - the user may have joined the campus
				// network or walked onto campus since.
				beginSession(false);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [available, autoStart, beginSession, clearDetectTimer]);

	// On by default. A page that loads while hidden (the shell rebuilding a WebView the OS
	// reclaimed in the background) starts from the visibility handler when it is first shown.
	useEffect(() => {
		if (!available || !autoStart || document.visibilityState === 'hidden') return;
		beginSession(false);
	}, [available, autoStart, beginSession]);

	const start = useCallback(() => {
		if (!available) return;
		stoppedRef.current = false;
		// Asking again is the user's answer to the permission message.
		deniedRef.current = false;

		if (!sessionRef.current) {
			beginSession(true);
			return;
		}

		// Join the session under way rather than restarting it: it may be moments from a fix.
		// Deliberately not gated on `active`: a silent session that registered but never
		// resolved a position is joined too, and gets the same clock as a fresh one.
		if (!silentRef.current) return;
		silentRef.current = false;
		if (detectingRef.current) awaitRequestedFix();
	}, [available, beginSession, awaitRequestedFix]);

	const stop = useCallback(() => {
		stoppedRef.current = true;
		pausedRef.current = null;
		clearDetectTimer();
		detectingRef.current = false;
		sessionRef.current = false;
		setLoading(false);
		setActive(false);
		setPosition(null);
		setHeading(null);
		sendToNative({ type: MessageTypes.STOP_POSITIONING });
	}, [clearDetectTimer]);

	return { available, position, heading, status, loading, error, active, start, stop, clearError };
}
